import React, { Fragment, useEffect, useState } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import FullstoryIcon from "@common/assets/images/integrations/fullstory-icon.svg";
import { DetailsBar } from "@common/components/DetailsBar/DetailsBar";
import { ActivityHistoryButton } from "@common/components/DetailsPane/ActivityHistoryButton";
import { DetailsPane } from "@common/components/DetailsPane/DetailsPane";
import { DetailsPaneNudge } from "@common/components/DetailsPane/DetailsPaneNudge";
import {
  getAppUserInfoElements,
  getExtensionsDropdown,
  getIntegrationDropdown,
  getTicketDetailsElements,
  GroupedInfoElement,
  GroupedTicketDetailsElement,
  InfoElement,
  IntegrationDropdownProps,
  isElementDisabled,
  readMetadata,
} from "@common/components/DetailsPane/helper";
import * as Icon from "@common/components/DetailsPane/icons";
import Heading from "@common/components/Heading/Heading";
import EditableTitle from "@common/components/Input/EditableTitle/EditableTitle";
import InputWithTags from "@common/components/Input/InputWithTags/InputWithTags";
import { RowType } from "@common/components/MasterTable/models/MasterTableModel";
import {
  ActivityHistoryNudge,
  IntegrationsNudge,
  MetadataNudge,
  UserDetailsNudge,
} from "@common/components/Nudge/Nudge";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useFeedbackAttributes } from "@common/hooks/filtering/modules/useFeedbackAttributes";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { PlatformName } from "@common/models";
import { getIntegrationTypeName, Integration, INTEGRATION_TO_HASH } from "@common/models/integrations";
import { Issue, IssueServiceHookPermalink } from "@common/models/issueTracking";
import { docLink } from "@common/util/docs";
import identifiers from "@common/util/identifiers.json";
import useCommonTagsApiConsumer from "@main/consumers/useCommonTagsApiConsumer";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAuthContext } from "@main/context/Auth/Auth.context";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import AttachmentsContainer from "@main/pages/shared/components/Attachments/AttachmentsContainer";
import CustomFields from "@main/pages/shared/components/CustomFields/CustomFields";
import LinkedTicketsTable from "@main/pages/shared/components/LinkedTicketsTable/LinkedTicketsTable";
import MessageContainer from "@main/pages/shared/components/Messages/MessageContainer";
import { SentryIntegrationContainer } from "@main/pages/shared/components/SentryIntegrationContainer/SentryIntegrationContainer";
import { SessionReplayContainer } from "@main/pages/shared/components/SessionReplayContainer/SessionReplayContainer";
import SheldonSummary from "@main/pages/shared/components/SheldonSummary/SheldonSummary";
import { TicketDetailsContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer";
import { TicketContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer.styles";
import { TicketInfoContainer } from "@main/pages/shared/components/TicketDetails/TicketInfoContainer";
import { TicketKeyCopy } from "@main/pages/shared/components/TicketKey/TicketKey";
import TicketMobileViewHeader from "@main/pages/shared/components/TicketMobileViewHeader/TicketMobileViewHeader";
import * as Styled from "@main/pages/shared/styles/DetailsWrapper.styles";
import { DuplicateOf } from "@main/pages/UserFeedback/components/DuplicateOf/DuplicateOf";
import { ParentBreadcrumb } from "@main/pages/UserFeedback/components/ParentBreadcrumb/ParentBreadcrumb";
import useUserFeedbackAiSummaryConsumer from "@main/pages/UserFeedback/consumers/useUserFeedbackAiSummaryConsumer";
import useUserFeedbackDetailsApiConsumer from "@main/pages/UserFeedback/consumers/useUserFeedbackDetailsApiConsumer";
import useUserFeedbackMessagesConsumer from "@main/pages/UserFeedback/consumers/useUserFeedbackMessagesConsumer";
import { RoutePaths } from "@main/router/config/routePaths";
import TagManager from "react-gtm-module";
import { generatePath, useNavigate, useParams } from "react-router-dom";

export const UserFeedbackDetailsPageView = () => {
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "User feedback details" });
  }, [sendEvent]);

  const params = useParams<{ userFeedbackKey: string }>();
  const navigate = useNavigate();

  const { addExternal } = useFeedbackAttributes(true);
  const [refreshActivity, setRefreshActivity] = useState(false);

  const { userData } = useAuthContext();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const consumer = useUserFeedbackDetailsApiConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    userFeedbackKey: params.userFeedbackKey,
    setRefreshActivity: setRefreshActivity,
  });
  const { messages, sendMessage, deleteMessage, editMessage } = useUserFeedbackMessagesConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    userFeedbackId: consumer.userFeedbackDetails?.id,
    activityRefresh: refreshActivity,
    setRefreshActivity: setRefreshActivity,
  });

  const { response, generateSummary } = useUserFeedbackAiSummaryConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    userFeedbackId: consumer.userFeedbackDetails?.id,
    enabled: Boolean(selectedApp?.ai_enabled),
  });

  const { commonTags } = useCommonTagsApiConsumer({ selectedWorkspaceId: selectedWorkspace?.id });
  const [detailDropdownOpened, setDetailDropdownOpened] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (selectedApp?.is_sample) return;
    TagManager.dataLayer({
      dataLayer: {
        event: "feedback_ticket_visit",
        user_id: userData?.id,
        workspace_id: selectedWorkspace?.id,
        email_address: userData?.email,
      },
    });
  }, [selectedApp?.is_sample, selectedWorkspace?.id, userData?.email, userData?.id]);

  const parentTicket = consumer.userFeedbackDetails?.parent;

  const CustomElement = consumer.userFeedbackDetails ? (
    <NavigationCustomElement>
      <div>
        <Tooltip
          text={"See all tickets"}
          position={"bottom"}
        >
          <Styled.NavbarNavigation
            onClick={() =>
              navigate(
                generatePath(RoutePaths.USER_FEEDBACK, {
                  workspaceSlug: selectedWorkspace?.slug ?? null,
                  appKey: selectedApp?.key ?? null,
                }),
              )
            }
          >
            <Icon.SendIcon
              height={20}
              width={20}
            />
          </Styled.NavbarNavigation>
        </Tooltip>
      </div>
      {parentTicket && <ParentBreadcrumb parent={parentTicket} />}
      <Styled.DetailsTitle data-testid={identifiers["navbar.detailsTitle"]}>
        <p>{consumer.userFeedbackDetails?.pretty_title}</p>
      </Styled.DetailsTitle>
      <TicketKeyCopy
        ticketKey={consumer.userFeedbackDetails?.key?.toString()}
        enableCopy={true}
        marginLeft={8}
      />
    </NavigationCustomElement>
  ) : (
    <Fragment />
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (consumer.error) {
    return (
      <ErrorStateView
        title="There was an error loading ticket. Try refreshing."
        subtitle="The link you followed may be broken, or the ticket may have been removed."
      />
    );
  }

  if (!consumer.userFeedbackDetails) {
    return <Spinner />;
  }

  const getSessionLinksElements = () => {
    return [
      {
        infoElements: [
          {
            tip: "Fullstory session",
            icon: FullstoryIcon,
            fullwidth: false,
            Dropdown: getExtensionsDropdown(
              consumer?.userFeedbackDetails?.pretty_fullstory_session ? "Fullstory" : "Not Available",
              "Fullstory",
              FullstoryIcon,
              {
                text: "Fullstory session",
                link: consumer?.userFeedbackDetails?.pretty_fullstory_session ?? "",
              },
              isElementDisabled(consumer?.userFeedbackDetails?.pretty_fullstory_session ?? ""),
              detailDropdownOpened,
              setDetailDropdownOpened,
            ),
          },
        ],
      },
    ];
  };

  const getAppIntegrations = (
    integrations?: Integration[],
    serviceHooks?: IssueServiceHookPermalink[],
  ): Array<GroupedTicketDetailsElement> => {
    if (!integrations || integrations.length == 0) return [];

    return [
      {
        infoElements: integrations.map((integration) => {
          const serviceHook = serviceHooks?.find((hook) => hook.service_hook_type === integration.type);
          const dropdownProps: IntegrationDropdownProps = {
            integration,
            serviceHook,
            integrationDropdownOpened: consumer.integrationDropdownOpened,
            setIntegrationDropdownOpened: consumer.setIntegrationDropdownOpened,
            sendIssue: consumer.sendIssue,
            sendingIntegrationMap: consumer.sendingIntegrationMap,
            onClickIntegrationSettings: () =>
              navigate(
                generatePath(RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK_KEY, {
                  workspaceSlug: selectedWorkspace?.slug ?? null,
                  appKey: selectedApp?.key ?? null,
                }).concat(`#${INTEGRATION_TO_HASH[integration.type]}`),
              ),
          };
          return {
            isError: false,
            tip: `${getIntegrationTypeName(integration.type)} integration`,
            Dropdown: getIntegrationDropdown(dropdownProps),
          } as InfoElement;
        }),
      },
    ];
  };

  const getMetadataElements = (metadata: { [key: string]: string } | undefined): Array<GroupedTicketDetailsElement> => {
    if (!metadata || Object.keys(metadata).length == 0) return [];

    return [
      {
        infoElements: readMetadata(metadata, [], detailDropdownOpened, setDetailDropdownOpened, addToFilterObject),
      },
    ];
  };

  const addToFilterObject = {
    addToFilter: addExternal,
    redirectURL: generatePath(RoutePaths.USER_FEEDBACK, {
      workspaceSlug: selectedWorkspace?.slug ?? null,
      appKey: selectedApp?.key ?? null,
    }),
  };

  const infoPane: GroupedInfoElement[] = [];
  infoPane.push({
    title: "Ticket details",
    headingElement: consumer?.userFeedbackDetails?.log ? (
      <ActivityHistoryButton
        to={generatePath(RoutePaths.USER_FEEDBACK_LOGS, {
          workspaceSlug: selectedWorkspace?.slug ?? null,
          appKey: selectedApp?.key ?? null,
          userFeedbackKey: params.userFeedbackKey ?? null,
        })}
      />
    ) : (
      <ActivityHistoryNudge />
    ),
    infoElements: getTicketDetailsElements(
      consumer.userFeedbackDetails,
      detailDropdownOpened,
      setDetailDropdownOpened,
      selectedApp,
      addToFilterObject,
      consumer.blackbox,
      consumer.issueDetailsNums,
    ),
  });
  if (selectedApp?.platform?.name === PlatformName.WEB)
    infoPane.push({
      title: "Session links",
      infoElements: getSessionLinksElements(),
    });
  infoPane.push({
    title: "Ticket integrations",
    infoElements: getAppIntegrations(selectedApp?.integrations, consumer.serviceHooks),
    link: {
      tooltipText: "Add integration",
      link: generatePath(RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK_KEY, {
        ...params,
        workspaceSlug: selectedWorkspace?.slug ?? null,
        appKey: selectedApp?.key ?? null,
      }),
      testId: identifiers["details.pane.integration.link.add"],
    },
    emptyView: (
      <DetailsPaneNudge>
        <IntegrationsNudge />
      </DetailsPaneNudge>
    ),
  });
  infoPane.push({
    title: "Ticket metadata",
    infoElements: getMetadataElements(consumer.userFeedbackDetails?.metadata_),
    link: {
      tooltipText: "Set ticket metadata",
      link: docLink(selectedApp?.platform.name).addTicketMetadata,
      isExternal: true,
      testId: identifiers["details.pane.metadata.link.add"],
    },
    emptyView: (
      <DetailsPaneNudge>
        <MetadataNudge />
      </DetailsPaneNudge>
    ),
  });

  infoPane.push({
    title: "User details",
    infoElements: getAppUserInfoElements(consumer.userFeedbackDetails?.app_user, () => {
      window.open(
        generatePath(RoutePaths.USER_DETAILS, {
          workspaceSlug: selectedWorkspace?.slug ?? null,
          appKey: selectedApp?.key ?? null,
          userId: consumer.userFeedbackDetails?.app_user?.id ?? null,
        }),
        "_blank",
      );
    }),
    link: {
      tooltipText: "Register app user",
      link: docLink(selectedApp?.platform.name).registerAppUser,
      isExternal: true,
    },
    emptyView: (
      <DetailsPaneNudge>
        <UserDetailsNudge />
      </DetailsPaneNudge>
    ),
  });

  const showDuplicate: boolean =
    (consumer.issueSuggestion &&
      consumer.userFeedbackDetails.suggest_duplicate &&
      !Boolean(consumer.userFeedbackDetails.linked_issues_count) &&
      !consumer.userFeedbackDetails.parent_id) ??
    false;

  return (
    <PageWrap>
      <TicketContainer>
        <TicketInfoContainer>
          <TicketMobileViewHeader app={selectedApp} />
          <Heading
            as="h2"
            heading2
            marginBottom={0}
          >
            <EditableTitle
              value={consumer.userFeedbackDetails?.pretty_title}
              defaultValue="No description"
              onEditConfirm={(title?: string) => consumer.updateUserFeedbackTitle({ title })}
              testId={identifiers["central.column.user.feedback.title"]}
            />
          </Heading>
          {consumer.userFeedbackDetails.parent && <DuplicateOf parent={consumer.userFeedbackDetails.parent} />}
          {!selectedApp?.is_sample && selectedApp?.ai_enabled && (
            <SheldonSummary
              llmResponse={response}
              userFeedbackId={consumer.userFeedbackDetails?.id}
              onGenerateResponse={generateSummary}
              onAddTag={consumer.createIssueTag}
              onChangePriority={consumer.changePriority}
              duplicateSuggestion={consumer.issueSuggestion}
              onMergeDuplicate={() =>
                consumer.onMergeAction([consumer.userFeedbackDetails as Issue], consumer.issueSuggestion as Issue)
              }
              onRemoveDuplicate={() => consumer.updateFeedbackSuggestion({ suggest_duplicate: false })}
              showDuplicate={showDuplicate}
              onAiAssistantClick={consumer.openAiAssistantWithPrompt}
              isPreparingAiPrompt={consumer.isLoadingActivityHistory}
            />
          )}
          <DetailsBar
            details={consumer.userFeedbackDetails}
            changeAssignee={consumer.changeAssignee}
            changePriority={consumer.changePriority}
            changeStatus={consumer.changeStatus}
            changePublic={() => consumer.updateUserFeedbackPublicAsync(!consumer.userFeedbackDetails?.public_key)}
            deleteTicket={consumer.deleteUserFeedback}
            onMergeAction={consumer.onMergeAction}
            type={RowType.USER_FEEDBACK}
            clearMerge={consumer.onUnMergeAction}
          />
          <InputWithTags
            commonTags={commonTags}
            selectedTags={consumer.userFeedbackDetails.tags}
            onAddTag={consumer.createIssueTag}
            onRemoveTag={consumer.deleteIssueTag}
          />
          <LinkedTicketsTable linkedTickets={consumer.linkedIssues} />
          <CustomFields customFields={consumer.userFeedbackDetails.custom_fields} />
          {consumer.userFeedbackDetails.session_replay_url && (
            <SessionReplayContainer sessionReplayUrl={consumer.userFeedbackDetails.session_replay_url} />
          )}
          <SentryIntegrationContainer
            sentryIntegration={selectedWorkspace?.sentry}
            fetchSentryProjects={consumer.getSentryProjects}
            fetchSentryIssues={consumer.getSentryIssues}
            selectedWorkspace={selectedWorkspace}
          />
          <AttachmentsContainer
            deviceOrientation={consumer.userFeedbackDetails.pretty_device_orientation}
            attachments={consumer.mappedAttachments.sort((a, b) => a.num - b.num)}
          />
          <MessageContainer
            messages={messages}
            onMessageSend={sendMessage}
            onMessageDelete={deleteMessage}
            onMessageEdit={editMessage}
            appUser={consumer.userFeedbackDetails.app_user}
          />
        </TicketInfoContainer>
        <TicketDetailsContainer>
          <DetailsPane
            info={infoPane}
            headerTestId={identifiers["user.feedback.details.pane.header"]}
          />
        </TicketDetailsContainer>
      </TicketContainer>
    </PageWrap>
  );
};
